import { MAXIMUM_DEPTH_OF_HISTORY } from '@chegg-wt/core-wa/constants/paraphraser'
import type { Step } from '@tiptap/pm/transform'
import {
  clearToast,
  setPaperRequiresRecheck,
  setRedoEnabled,
  setUndoEnabled,
} from '@chegg-wt/core-wa/state/actions'
import { store } from '@chegg-wt/core/state'
import { debounceOnSavePaper } from '@chegg-wt/core-wa/state/utils'
import { SentenceOffset } from '@chegg-wt/core-wa/state/types'
import { Feedback, FeedbackType } from '@chegg-wt/core-wa/types'
import { sentenceOffsetsSelector } from '@chegg-wt/core-wa/state/selectors'
import { Stack } from './stack'
import {
  FeedbackDelta,
  HistoryChangeType,
  HistoryCurrentVersion,
  HistoryDeltaAction,
  HistoryRemoveGrammarCards,
  HistoryRemoveParaphraseCards,
  HistoryVersion,
  HistoryInteractionType,
  SentencesDelta,
  HistoryChangeDetails,
} from './types'
import {
  getHistoryVersionWithStepInJson,
  mergeSteps,
  applyStepsAndGetInversion,
  undoFeedbackDelta,
  redoFeedbackDelta,
  removeOldFeedbacks,
  addNewFeedbacks,
  addDiscardedFeedbacksForFiltering,
  getKeyFromSentence,
  getFeedbackKeyFromPosition,
  applySentenceChange,
  applySentenceOffsetOnManualChange,
  closeQuickActionRighPanelIfOpen,
} from './utils'
import { sendHistoryUndoRedoClickEvent } from './rioUtils'

class History {
  private undo: Stack<HistoryVersion>

  private redo: Stack<HistoryVersion>

  private removedGrammarCards: HistoryRemoveGrammarCards

  private removedParaphraseCards: HistoryRemoveParaphraseCards

  private currentVersion: HistoryCurrentVersion

  constructor() {
    this.undo = new Stack()
    this.redo = new Stack()
    this.refreshCurrentVersion()
    this.removedGrammarCards = new Map()
    this.removedParaphraseCards = new Map()
  }

  private refreshCurrentVersion = () => {
    this.currentVersion = {
      removedGrammarFeedbacks: new Set<String>(),
      removedParaphraseFeedbacks: new Set<String>(),
      addedGrammarFeedbacks: new Set<String>(),
      addedParaphraseFeedbacks: new Set<String>(),
      steps: [],
      feedbackDelta: [],
      sentencesDelta: null,
      sentencesRemovedOnManualChange: [],
      changeDetails: null,
    }
  }

  // Commit the current version data and create a new history version
  public commit = () => {
    // If current version is empty, ignore it
    if (
      this.currentVersion.steps.length === 0 &&
      this.currentVersion.feedbackDelta.length === 0
    ) {
      return
    }
    // This is optimization, we will merge adjacent deltas to have less number of changes
    this.currentVersion.steps = mergeSteps(this.currentVersion.steps.reverse()).reverse()

    if (this.undo.size >= MAXIMUM_DEPTH_OF_HISTORY) {
      // We will clear last versions and only keep track of latest of max depth allowed
      this.undo.removeBottom()
    }
    this.undo.push(getHistoryVersionWithStepInJson(this.currentVersion))

    // Clear next versions on any change
    this.redo.clear()

    // Clear current version to collect new changes
    this.refreshCurrentVersion()

    // Handle enable/disable undo/redo buttons
    store.dispatch(setUndoEnabled(true))
    store.dispatch(setRedoEnabled(false))
  }

  // pop out from undo stack and apply undo
  public applyUndo = (isKeyboardEvent?: boolean) => {
    // If we have any steps collected in current version, add that as state
    this.commit()
    store.dispatch(clearToast())

    if (this.undo.isEmpty()) {
      // Do nothing if user do undo when we have no undo versions
      return null
    }
    const historyVersion = this.undo.pop()
    sendHistoryUndoRedoClickEvent(
      true,
      store.dispatch,
      isKeyboardEvent,
      historyVersion.changeDetails
    )

    closeQuickActionRighPanelIfOpen(store.getState(), store.dispatch)

    // Enable/Disable review button
    if (typeof historyVersion.enableReviewButton === 'boolean') {
      store.dispatch(setPaperRequiresRecheck(!historyVersion.enableReviewButton))
    }

    // Apply the setnence offsets change
    applySentenceChange(
      historyVersion.sentencesDelta,
      store.getState,
      store.dispatch,
      true
    )

    // Remove the feedbacks for this sentence
    removeOldFeedbacks(
      historyVersion.addedGrammarFeedbacks,
      historyVersion.addedParaphraseFeedbacks,
      store.getState(),
      store.dispatch
    )
    // Apply text changes in reverse direction
    const invertedSteps = applyStepsAndGetInversion(
      historyVersion.stepsJson.reverse()
    ).reverse()
    historyVersion.stepsJson = invertedSteps
    // Add sentences which were removed when we do some manual change
    applySentenceOffsetOnManualChange(
      historyVersion.sentencesRemovedOnManualChange,
      store.getState,
      store.dispatch,
      true
    )
    // Apply feedback card changes
    undoFeedbackDelta({
      feedbackDelta: historyVersion.feedbackDelta,
      state: store.getState(),
      dispatch: store.dispatch,
      removedGrammarCards: this.removedGrammarCards,
      removedParaphraseCards: this.removedParaphraseCards,
    })
    // Fetch the feedbacks for this new sentence after change.
    const updatedState = store.getState()
    addNewFeedbacks(
      historyVersion.removedGrammarFeedbacks,
      historyVersion.removedParaphraseFeedbacks,
      updatedState,
      store.dispatch
    )

    this.redo.push(historyVersion)

    // Save paper
    debounceOnSavePaper(store.dispatch)

    // Handle enable/disable undo/redo buttons
    store.dispatch(setRedoEnabled(true))
    store.dispatch(setUndoEnabled(this.undo.size > 0))
  }

  // pop out from redo stack and apply redo
  public applyRedo = (isKeyboardEvent?: boolean) => {
    store.dispatch(clearToast())
    if (this.redo.isEmpty()) {
      // Do nothing if user do redo when we have no redo versions
      return null
    }
    const historyVersion = this.redo.pop()
    sendHistoryUndoRedoClickEvent(
      false,
      store.dispatch,
      isKeyboardEvent,
      historyVersion.changeDetails
    )

    closeQuickActionRighPanelIfOpen(store.getState(), store.dispatch)

    // Enable/Disable review button
    if (typeof historyVersion.enableReviewButton === 'boolean') {
      store.dispatch(setPaperRequiresRecheck(historyVersion.enableReviewButton))
    }
    // Remove the feedbacks for this sentence
    removeOldFeedbacks(
      historyVersion.removedGrammarFeedbacks,
      historyVersion.removedParaphraseFeedbacks,
      store.getState(),
      store.dispatch
    )
    // Apply text changes
    const invertedSteps = applyStepsAndGetInversion(historyVersion.stepsJson)
    historyVersion.stepsJson = invertedSteps

    // Remove sentences which were removed when we do some manual change
    applySentenceOffsetOnManualChange(
      historyVersion.sentencesRemovedOnManualChange,
      store.getState,
      store.dispatch,
      false
    )

    // Apply feedback card changes
    redoFeedbackDelta({
      feedbackDelta: historyVersion.feedbackDelta,
      dispatch: store.dispatch,
      state: store.getState(),
      removedGrammarCards: this.removedGrammarCards,
      removedParaphraseCards: this.removedParaphraseCards,
    })
    // Fetch the feedbacks for this new sentence after change.
    const updatedState = store.getState()
    addNewFeedbacks(
      historyVersion.addedGrammarFeedbacks,
      historyVersion.addedParaphraseFeedbacks,
      updatedState,
      store.dispatch
    )

    // Apply the setnence offsets change
    applySentenceChange(
      historyVersion.sentencesDelta,
      store.getState,
      store.dispatch,
      false
    )

    this.undo.push(historyVersion)

    // Save paper
    debounceOnSavePaper(store.dispatch)

    // Handle enable/disable undo/redo buttons
    store.dispatch(setUndoEnabled(true))
    store.dispatch(setRedoEnabled(this.redo.size > 0))
  }

  // Clear the history, used on clicking review
  public clear = () => {
    this.undo.clear()
    this.redo.clear()
    this.refreshCurrentVersion()
    this.removedGrammarCards = new Map()
    this.removedParaphraseCards = new Map()
    store.dispatch(setUndoEnabled(false))
    store.dispatch(setRedoEnabled(false))
  }

  public addStep = (step: Step) => {
    this.redo.clear()
    this.currentVersion.steps.push(step)
  }

  public setEnableReviewButton = (enableButton: boolean) => {
    this.currentVersion.enableReviewButton = enableButton
  }

  public addFeedbackChange = (feedbackDelta: FeedbackDelta, isFlagged?: boolean) => {
    // commit old data so that we can create a separate state for remove/apply card
    this.commit()
    const { action: deltaAction, feedback } = feedbackDelta
    const discardAction = isFlagged
      ? HistoryInteractionType.FLAG_SUGGESTION
      : HistoryInteractionType.SUGGESTION_REJECTED
    const action =
      deltaAction === HistoryDeltaAction.DISCARD
        ? discardAction
        : HistoryInteractionType.SUGGESTION_ACCEPTED

    this.currentVersion.changeDetails = {
      type:
        feedback.feedbackType === FeedbackType.GRAMMAR
          ? HistoryChangeType.GRAMMAR
          : HistoryChangeType.CLARITY,
      action,
      id: feedback.id,
    }

    this.currentVersion.feedbackDelta.push(feedbackDelta)
    if (feedbackDelta.action === HistoryDeltaAction.DISCARD) {
      // Special case to handle discard
      const state = store.getState()
      const sentenceOffsets = sentenceOffsetsSelector(state)
      addDiscardedFeedbacksForFiltering(
        sentenceOffsets,
        feedbackDelta.feedback,
        this.removedGrammarCards,
        this.removedParaphraseCards
      )
    }
  }

  public handleRemoveFeedbackSentences = (sentenceIds: SentenceOffset[]) => {
    // Not checking nonManualUpdateType but feedback type from here as PARAPHRASE type is common in rewrite, expand and short in nonManualUpdateType
    const feedbackType = this.currentVersion.feedbackDelta?.[0]?.feedback?.feedbackType

    sentenceIds.forEach((sentence) => {
      if (feedbackType !== FeedbackType.GRAMMAR) {
        this.currentVersion.addedGrammarFeedbacks.delete(sentence.id)
        this.currentVersion.removedGrammarFeedbacks.add(sentence.id)
      }
      if (feedbackType !== FeedbackType.PARAPHRASE) {
        this.currentVersion.addedParaphraseFeedbacks.delete(sentence.id)
        this.currentVersion.removedParaphraseFeedbacks.add(sentence.id)
      }
    })
  }

  public addGrammarFeedbackSentences = (sentenceIds: string[]) => {
    sentenceIds.forEach((id) => {
      // Add to set of adding feedback sentence ids
      this.currentVersion.addedGrammarFeedbacks.add(id)
    })
  }

  public addParaphraseFeedbackSentences = (sentenceIds: string[]) => {
    sentenceIds.forEach((id) => {
      // Add to set of adding feedback sentence ids
      this.currentVersion.addedParaphraseFeedbacks.add(id)
    })
  }

  filterRemovedGrammarCardsFromFeedbacks = (
    sentenceId: string,
    sentenceText: string,
    feedbacks: Feedback[]
  ): Feedback[] => {
    const key = getKeyFromSentence(sentenceId, sentenceText)
    const removedGrammarCardsForThisSentence = this.removedGrammarCards.get(key)
    if (!removedGrammarCardsForThisSentence) return feedbacks
    return feedbacks.filter(
      (feedback) =>
        !removedGrammarCardsForThisSentence.has(
          getFeedbackKeyFromPosition(feedback.position)
        )
    )
  }

  filterRemovedParaphraseCardsFromFeedbacks = (
    sentenceId: string,
    sentenceText: string
  ): boolean => {
    const key = getKeyFromSentence(sentenceId, sentenceText)
    return Boolean(this.removedParaphraseCards.get(key))
  }

  public addSentenceOffsetDelta(sentenceDelta: SentencesDelta) {
    // We don't want to store in history if there is no change in sentences
    if (
      sentenceDelta.addedSentences.length === 0 &&
      sentenceDelta.removedSentences.length === 0
    )
      return

    // Commit if we have already some pending item in history
    this.commit()
    // If we don't have any undo state, we don't require to have this change in history
    if (this.undo.size === 0) return

    const lastVersion = this.undo.peek()

    lastVersion.sentencesDelta = sentenceDelta
  }

  public addSentencesRemovedOnManualEdit = (
    sentencesRemoved: SentencesDelta['removedSentences']
  ) => {
    if (sentencesRemoved.length === 0) return
    this.currentVersion.sentencesRemovedOnManualChange.push(...sentencesRemoved)
  }

  public addHistoryChangeDetails = (changeDetails: HistoryChangeDetails) => {
    this.currentVersion.changeDetails = changeDetails
  }
}

const history = new History()
export default history
